/**
 * Format booking datetime elements in babysitter timezone
 */
export const formatBookingDatetime = (wrapper: HTMLElement) => {
  const startDate = wrapper.querySelector(".start-date") as HTMLElement;
  const startTime = wrapper.querySelector(".start-time") as HTMLElement;
  const endTime = wrapper.querySelector(".end-time") as HTMLElement;

  if (!startDate?.dataset.datetime || !startTime?.dataset.datetime || !endTime?.dataset.datetime) {
    return;
  }

  // Get babysitter timezone from data attribute
  const babysitterTimezone = wrapper.dataset.babysitterTimezone;
  const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (!babysitterTimezone || !currentTimezone) {
    return;
  }

  const bookingDate = new Date(startDate.dataset.datetime);

  // Get timezone abbreviations for comparison
  const currentTzAbbr = new Intl.DateTimeFormat(undefined, {
    timeZone: currentTimezone,
    timeZoneName: "short",
  })
    .formatToParts(bookingDate)
    .find((part) => part.type === "timeZoneName")?.value;

  const babysitterTzAbbr = new Intl.DateTimeFormat(undefined, {
    timeZone: babysitterTimezone,
    timeZoneName: "short",
  })
    .formatToParts(bookingDate)
    .find((part) => part.type === "timeZoneName")?.value;

  // Check if timezones are different
  const timezoneMismatch = currentTzAbbr !== babysitterTzAbbr;

  // Display start date in babysitter timezone
  const startDateTime1 = new Date(startDate.dataset.datetime);
  startDate.textContent = new Intl.DateTimeFormat(undefined, {
    dateStyle: "full",
    timeZone: babysitterTimezone,
  }).format(startDateTime1);

  // Always display times in babysitter timezone
  const startDateTime2 = new Date(startTime.dataset.datetime);
  startTime.textContent = new Intl.DateTimeFormat(undefined, {
    timeStyle: "short",
    timeZone: babysitterTimezone,
  }).format(startDateTime2);

  // Add timezone abbreviation if there's a mismatch
  if (timezoneMismatch && babysitterTzAbbr) {
    startTime.textContent += ` ${babysitterTzAbbr}`;
  }

  const endTimeOriginal = wrapper.querySelector(".end-time-original") as HTMLElement;
  if (endTimeOriginal?.dataset.datetime) {
    const endTimeOriginalDateTime = new Date(endTimeOriginal.dataset.datetime);
    endTimeOriginal.textContent = new Intl.DateTimeFormat(undefined, {
      timeStyle: "short",
      timeZone: babysitterTimezone,
    }).format(endTimeOriginalDateTime);

    // Add timezone abbreviation if there's a mismatch
    if (timezoneMismatch && babysitterTzAbbr) {
      endTimeOriginal.textContent += ` ${babysitterTzAbbr}`;
    }
  }

  const endDateTime = new Date(endTime.dataset.datetime);
  endTime.textContent = new Intl.DateTimeFormat(undefined, {
    timeStyle: "short",
    timeZone: babysitterTimezone,
  }).format(endDateTime);

  // Add timezone abbreviation if there's a mismatch
  if (timezoneMismatch && babysitterTzAbbr) {
    endTime.textContent += ` ${babysitterTzAbbr}`;
  }

  // Show timezone mismatch alert if timezones are different (only for single booking view)
  if (timezoneMismatch) {
    const timezoneAlert = wrapper.querySelector(".timezone-mismatch-alert") as HTMLElement;
    if (timezoneAlert) {
      timezoneAlert.classList.remove("d-none");
    }
  }
};

/**
 * Initialize booking datetime formatting for both single and collection views
 */
export const initBookingResource = () => {
  const bookingDatetimeWrappers: NodeListOf<HTMLElement> = document.querySelectorAll(
    ".booking-datetime-wrapper",
  );

  bookingDatetimeWrappers.forEach((wrapper) => {
    formatBookingDatetime(wrapper);
  });
};
